import { IS_DEV } from 'config';
import { useUser } from 'features/auth/hooks/useUser';
import { iframePaths } from 'modules/iframe/utils';
import { useCheckAccess } from '../components/AccessControl';
import { useFeatureFlag } from './useFeatureFlag';
import { useLoggedInUser } from './useLoggedInUser';
import { useModule } from './useModule';
import { useOrgType } from './useOrg';
import { evaluateNavGuard } from '../utils/navigation';
import type { NavItem } from '../types/navigation';

/** Whether the current user may see a navigation entry, per its declared gates. */
export const useNavItemAccess = (item: NavItem) => {
  const { data: user } = useUser();
  const { modules } = useLoggedInUser();
  const isSupplier = useOrgType(2);
  const hasFeatureFlag = useFeatureFlag(item.featureflag ?? '');
  const hasScope = useCheckAccess(false, item.url);
  const hasModuleAccess = useModule(item.module ?? '');
  const passesGuard = evaluateNavGuard(item.guard, {
    roleNames: user?.loginContext?.roleNames ?? null,
    modules,
    orgType: user?.loginContext?.organizationType,
  });

  if (item.devOnly && !IS_DEV) return false;
  // iframe pages are not available for supplier portal accounts
  if (iframePaths.includes(item.url) && isSupplier) return false;
  if (item.featureflag && !hasFeatureFlag) return false;
  // RBAC check
  if (!hasScope) return false;
  // module check, supplier portal accounts have access to all
  if (item.module && !hasModuleAccess && !isSupplier) return false;
  if (item.accessTo === 'supplierOnly' && !isSupplier) return false;
  if (item.accessTo === 'designerOnly' && isSupplier) return false;
  return passesGuard;
};
